import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import { onEnterKeyPress } from 'app/blocks/common/key-utils';
import { compose, validation } from 'app/blocks/common/utils';
import PromptAction from 'app/blocks/Prompt/_action/prompt_action';
import OrdersPanelEditAddress from 'app/pages/orders/panel/edit-address';
import CheckboxButton from 'app/ui/form/CheckboxButton';

function withOrdersPanelContactContainer(Component) {
    return function OrdersPanelContactContainer({ addressesState, controller, user, ...props }) {
        const { mainAddress, secondaryAddress } = addressesState;
        const addressesAreEmpty = ![mainAddress, secondaryAddress].some(validation.notEmpty);

        if (addressesAreEmpty) {
            mainAddress.primaryEmail = user.primaryEmail;
            mainAddress.fullName = user.name;
            secondaryAddress.primaryEmail = user.primaryEmail;
            secondaryAddress.fullName = user.name;
        }

        return (
            <Component
                {...props}
                addressesProvider={controller.addressesProvider}
                isSecondaryAddressTheSame={addressesState.isSecondaryAddressTheSame}
                isUpdateFromProfileButtonEnabled={controller.isUpdateFromProfileButtonEnabled()}
                mainAddress={mainAddress}
                secondaryAddress={secondaryAddress}
                updateMainAddress={controller.updateMainAddress}
                updateMainAddressFromProfile={controller.updateMainAddressFromProfile}
                updateSecondaryAddress={controller.updateSecondaryAddress}
                updateSecondaryAddressFromProfile={controller.updateSecondaryAddressFromProfile}
                updateSecondaryAddressTheSame={controller.handleAddressFlagChange}
            />
        );
    };
}

class OrdersPanelContact extends React.PureComponent {
    static propTypes = {
        addressesProvider: PropTypes.shape({}).isRequired,
        className: PropTypes.string,
        isOpenAccessOrder: PropTypes.bool,
        isSecondaryAddressTheSame: PropTypes.bool.isRequired,
        isUpdateFromProfileButtonEnabled: PropTypes.bool.isRequired,
        l: PropTypes.func.isRequired,
        mainAddress: PropTypes.shape({}).isRequired,
        secondaryAddress: PropTypes.shape({}).isRequired,
        showSecondaryAddress: PropTypes.bool,
        updateMainAddress: PropTypes.func.isRequired,
        updateMainAddressFromProfile: PropTypes.func.isRequired,
        updateSecondaryAddress: PropTypes.func.isRequired,
        updateSecondaryAddressFromProfile: PropTypes.func.isRequired,
        updateSecondaryAddressTheSame: PropTypes.func.isRequired,
        validationResults: PropTypes.shape({}).isRequired,
    };

    static defaultProps = {
        className: undefined,
        isOpenAccessOrder: false,
        showSecondaryAddress: false,
    };

    render() {
        const {
            addressesProvider,
            className,
            showSecondaryAddress,
            isOpenAccessOrder,
            isSecondaryAddressTheSame,
            isUpdateFromProfileButtonEnabled,
            l,
            mainAddress,
            secondaryAddress,
            updateMainAddress,
            updateMainAddressFromProfile,
            updateSecondaryAddress,
            updateSecondaryAddressFromProfile,
            updateSecondaryAddressTheSame,
            validationResults,
        } = this.props;

        return (
            <div className={className}>
                <div className="border_top" />
                <div className="row">
                    <div className="col-md-6 m-btm_micro text_md bluetxt">
                        {showSecondaryAddress && 'Contact address'}
                    </div>
                    {isUpdateFromProfileButtonEnabled && (
                        <div className="col-md-6 text-right">
                            <a
                                className="bold_text bluetxt"
                                data-seleniumid="main-address-useProfileAddress-link"
                                onClick={updateMainAddressFromProfile}
                                onKeyPress={onEnterKeyPress(updateMainAddressFromProfile)}
                                role="button"
                                tabIndex={-1}
                            >
                                {l('ORDER_PANELS.PANEL_CONTACT.USE_PROFILE_ADDRESS')}
                            </a>
                        </div>
                    )}
                </div>
                {showSecondaryAddress && <div className="border_top" />}
                <OrdersPanelEditAddress
                    address={mainAddress}
                    handleAddressChange={updateMainAddress}
                    isOpenAccessOrder={isOpenAccessOrder}
                    seleniumid="main-address"
                    supportDataProvider={addressesProvider}
                    validationResults={get(validationResults, 'mainAddress')}
                />

                {showSecondaryAddress && (
                    <>
                        <div className="border_top" />
                        <div className="row">
                            <div className="col-12 pay-mode-container">
                                <CheckboxButton
                                    checked={isSecondaryAddressTheSame}
                                    data-seleniumid="main-address-TheSame-checkbox-input"
                                    id="main-address-TheSame-checkbox"
                                    label="Billing address is the same as contact address"
                                    onChange={e => updateSecondaryAddressTheSame(e.target.checked)}
                                />
                                <PromptAction
                                    className="base__i-block"
                                    html={l('TOOLTIP.billingAddress')}
                                    seleniumid="main-address-TheSame__prompt-icon"
                                />
                            </div>
                        </div>

                        {!isSecondaryAddressTheSame && (
                            <>
                                <div className="border_top m-top_big" />
                                <div className="row">
                                    <div className="col-md-6 m-btm_micro text_md bluetxt">Billing address</div>
                                    {isUpdateFromProfileButtonEnabled && (
                                        <div className="col-md-6 text-right">
                                            <a
                                                className="bold_text bluetxt"
                                                data-seleniumid="secondary-address-useProfileAddress-link"
                                                onClick={updateSecondaryAddressFromProfile}
                                                onKeyPress={onEnterKeyPress(updateSecondaryAddressFromProfile)}
                                                role="button"
                                                tabIndex={-1}
                                            >
                                                {l('ORDER_PANELS.PANEL_CONTACT.USE_PROFILE_ADDRESS')}
                                            </a>
                                        </div>
                                    )}
                                </div>
                                <div className="border_top" />
                                <OrdersPanelEditAddress
                                    address={secondaryAddress}
                                    handleAddressChange={updateSecondaryAddress}
                                    seleniumid="secondary-address"
                                    showTooltip={isOpenAccessOrder}
                                    supportDataProvider={addressesProvider}
                                    validationResults={get(validationResults, 'secondaryAddress')}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        );
    }
}

export { OrdersPanelContact, withOrdersPanelContactContainer };
export default compose(withOrdersPanelContactContainer, withCodes2(ID.ORDER_PANELS, ID.TOOLTIP))(OrdersPanelContact);
